import { useEffect } from "react";
import type React from "react";
import { useDispatch } from "react-redux";
import { setPageHeader, clearPageHeader } from "../Redux/Slice/PageHeaderSlice";
import { usePageHeaderActionsDispatch } from "../Components/PageHeaderActionsProvider";

type UsePageHeaderOptions = {
	title: string;
	actions?: React.ReactNode | null;
	/** When false, the hook is inert : used when a page component is embedded (e.g. inside a modal)
	 *  and must not hijack the global page header. Defaults to true. */
	enabled?: boolean;
};

/**
 * Declares the global page header for the calling page's lifetime and cleans up on unmount so a
 * page can never leak its title/buttons onto the next route. The title goes through Redux
 * (PageHeaderSlice) but `actions` are React nodes, so they travel through
 * PageHeaderActionsProvider context instead of the serializable store. `actions` is a dependency:
 * callers that rebuild the node each render re-run the effect, which is what keeps button state
 * (disabled, spinners) live in the header.
 */
export const usePageHeader = ({ title, actions = null, enabled = true }: UsePageHeaderOptions) => {
	const dispatch = useDispatch();
	const setActions = usePageHeaderActionsDispatch();

	useEffect(() => {
		if (!enabled) return;
		dispatch(setPageHeader({ title }));
		setActions(actions);
		return () => {
			dispatch(clearPageHeader());
			setActions(null);
		};
	}, [dispatch, title, actions, setActions, enabled]);
};
